import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { DialogProvider } from "@/providers/DialogProvider";
import DashboardLayout from "@/layouts/DashboardLayout";

// Lazy-loaded pages — each becomes its own JS chunk
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const ClaimsPage = lazy(() => import("@/pages/ClaimsPage"));
const BarsPage = lazy(() => import("@/pages/BarsPage"));
const BarDetailPage = lazy(() => import("@/pages/BarDetailPage"));
const DrinksPage = lazy(() => import("@/pages/DrinksPage"));
const UsersPage = lazy(() => import("@/pages/UsersPage"));
const VerificationsPage = lazy(() => import("@/pages/VerificationsPage"));
const EventsPage = lazy(() => import("@/pages/EventsPage"));
const OffersPage = lazy(() => import("@/pages/OffersPage"));
const StoriesPage = lazy(() => import("@/pages/StoriesPage"));
const PlatformPage = lazy(() => import("@/pages/PlatformPage"));
const BreweryAnalyticsPage = lazy(() => import("@/pages/BreweryAnalyticsPage"));
const BarPriceMapPage = lazy(() => import("@/pages/BarPriceMapPage"));
const ModerationPage = lazy(() => import("@/pages/ModerationPage"));
const FeedbackPage = lazy(() => import("@/pages/FeedbackPage"));
const FeatureRequestsPage = lazy(() => import("@/pages/FeatureRequestsPage"));
const BugReportsPage = lazy(() => import("@/pages/BugReportsPage"));
const ContentReportsPage = lazy(() => import("@/pages/ContentReportsPage"));
const SportLeagues = lazy(() => import("@/pages/SportLeagues"));
const TrialManagementPage = lazy(() => import("@/pages/TrialManagementPage"));
const SubscriptionPaymentsPage = lazy(() => import("@/pages/SubscriptionPaymentsPage"));
const PricingConfigPage = lazy(() => import("@/pages/PricingConfigPage"));
const InvoicesPage = lazy(() => import("@/pages/InvoicesPage"));
const NotificationsTestPage = lazy(() => import("@/pages/NotificationsTestPage"));
const NotificationTemplatesPage = lazy(() => import("@/pages/NotificationTemplatesPage"));
const AdminRolePage = lazy(() => import("@/pages/AdminRolePage"));
const InvitationsPage = lazy(() => import("@/pages/InvitationsPage"));
const InviteRegisterPage = lazy(() => import("@/pages/InviteRegisterPage"));
const DrinksCatalogPage = lazy(() => import("@/pages/DrinksCatalogPage"));
const DrinkSizesCatalogPage = lazy(() => import("@/pages/DrinkSizesCatalogPage"));
const BrandCatalogPage = lazy(() => import("@/pages/BrandCatalogPage"));
const AmenitiesCatalogPage = lazy(() => import("@/pages/AmenitiesCatalogPage"));
const AudienceCatalogPage = lazy(() => import("@/pages/AudienceCatalogPage"));
const AtmosphereCatalogPage = lazy(() => import("@/pages/AtmosphereCatalogPage"));
const CompanySettingsPage = lazy(() => import("@/pages/CompanySettingsPage"));
const BarSuggestionsPage = lazy(() => import("@/pages/BarSuggestionsPage"));
const OsmExplorerPage = lazy(() => import("@/pages/OsmExplorerPage"));
const N8nWebhooksPage = lazy(() => import("@/pages/N8nWebhooksPage"));
const OsmApiOverviewPage = lazy(() => import("@/pages/OsmApiOverviewPage"));
const AnnouncementsPage = lazy(() => import("@/pages/AnnouncementsPage"));

function PageLoader() {
  return (
    <div className="flex h-64 items-center justify-center">
      <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isAdmin, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary-500 border-t-transparent mx-auto"></div>
          <p className="mt-4 text-gray-600">Lade...</p>
        </div>
      </div>
    );
  }

  if (!user || !isAdmin) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function LoginRoute() {
  const { user, isAdmin, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary-500 border-t-transparent mx-auto"></div>
          <p className="mt-4 text-gray-600">Lade...</p>
        </div>
      </div>
    );
  }

  if (user && isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <LoginPage />
    </Suspense>
  );
}

function App() {
  return (
    <DialogProvider>
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />

            <Route path="/login" element={<LoginRoute />} />

            <Route path="/invite/:token" element={<InviteRegisterPage />} />

            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<DashboardPage />} />
              <Route path="claims" element={<ClaimsPage />} />
              <Route path="bars" element={<BarsPage />} />
              <Route path="bars/:barId" element={<BarDetailPage />} />
              <Route path="drinks" element={<DrinksPage />} />
              <Route path="users" element={<UsersPage />} />
              <Route path="verifications" element={<VerificationsPage />} />
              <Route path="events" element={<EventsPage />} />
              <Route path="offers" element={<OffersPage />} />
              <Route path="stories" element={<StoriesPage />} />
              <Route path="analytics" element={<PlatformPage />} />
              <Route path="analytics/breweries" element={<BreweryAnalyticsPage />} />
              <Route path="analytics/bar-prices" element={<BarPriceMapPage />} />
              {/* Alte Analytics-URLs (Lesezeichen) */}
              <Route path="analytics/investor" element={<Navigate to="/dashboard/analytics?tab=growth" replace />} />
              <Route path="analytics/user-sources" element={<Navigate to="/dashboard/analytics?tab=growth" replace />} />
              <Route path="moderation" element={<ModerationPage />} />
              <Route path="feedback" element={<FeedbackPage />} />
              <Route path="feature-requests" element={<FeatureRequestsPage />} />
              <Route path="bug-reports" element={<BugReportsPage />} />
              <Route path="content-reports" element={<ContentReportsPage />} />
              <Route path="sport-leagues" element={<SportLeagues />} />
              <Route path="subscriptions">
                <Route index element={<Navigate to="trial" replace />} />
                <Route path="trial" element={<TrialManagementPage />} />
                <Route path="payments" element={<SubscriptionPaymentsPage />} />
                <Route path="pricing" element={<PricingConfigPage />} />
                <Route path="invoices" element={<InvoicesPage />} />
              </Route>
              <Route
                path="trial-management"
                element={<Navigate to="/dashboard/subscriptions/trial" replace />}
              />
              <Route path="notifications-test" element={<NotificationsTestPage />} />
              <Route path="notification-templates" element={<NotificationTemplatesPage />} />
              <Route path="developer/n8n-webhooks" element={<N8nWebhooksPage />} />
              <Route path="developer/osm-api" element={<OsmApiOverviewPage />} />
              <Route path="admin-roles" element={<AdminRolePage />} />
              <Route path="invitations" element={<InvitationsPage />} />
              <Route path="drinks-catalog" element={<DrinksCatalogPage />} />
              <Route path="drink-sizes-catalog" element={<DrinkSizesCatalogPage />} />
              <Route path="brand-catalog" element={<BrandCatalogPage />} />
              <Route path="amenities-catalog" element={<AmenitiesCatalogPage />} />
              <Route path="audiences-catalog" element={<AudienceCatalogPage />} />
              <Route path="atmospheres-catalog" element={<AtmosphereCatalogPage />} />
              <Route path="bar-suggestions" element={<BarSuggestionsPage />} />
              <Route path="osm-explorer" element={<OsmExplorerPage />} />
              <Route path="company-settings" element={<CompanySettingsPage />} />
              <Route path="announcements" element={<AnnouncementsPage />} />
            </Route>

            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </DialogProvider>
  );
}

export default App;
